import { inject } from 'inversify';

import { STATUS } from '@/constants';
import { service } from '@/decorators/service';
import { SignalrListener, SignalrMessage } from '@/decorators/signalr';
import CONSTANTS from '@/helpers/constants';
import { ConfigurationManager } from '@/managers/ConfigurationManager';
import { Logger } from '@/services/Logger';
import { Signalr } from '@/services/signalr/Signalr';
import { CallSourceStore } from '@/stores/CallSourceStore';
import { orderByField } from '@/utils/orderBy';
import { ICallSourceInfo } from '~/api/ICallSourceInfo';
import type { ICallSourceRespMsg } from '~/api/messages/ICallSourceRespMsg';
import type { ICallSourceUpdateRespMsg } from '~/api/messages/ICallSourceUpdateRespMsg';

@SignalrListener
@service(CallSourceManager)
export class CallSourceManager {
  @inject(Logger) protected logger: Logger;
  @inject(Signalr) protected signalr: Signalr;
  @inject(CallSourceStore) protected callSourceStore: CallSourceStore;
  @inject(ConfigurationManager) protected configurationManager: ConfigurationManager;

  get defaultCallSource(): string | undefined {
    return this.configurationManager.find(CONSTANTS.CONFIGURATIONS.DEFAULT_CALL_SOURCE)?.value;
  }

  get enabled(): ICallSourceInfo[] {
    const callSources = this.callSourceStore.all().filter(
      (callSource) => `${callSource.status}` === STATUS.ENABLED,
    );
    return orderByField(callSources, this.callSourceStore.valueKey);
  }

  find(id?: string): ICallSourceInfo | undefined {
    if (!id) {
      return undefined;
    }
    return this.callSourceStore.find(id);
  }

  getDefaultCallSource(): ICallSourceInfo | undefined {
    const defaultId = this.defaultCallSource;
    if (!defaultId) {
      return undefined;
    }
    return this.find(defaultId);
  }

  isDisabled(id?: string): boolean {
    const callSource = this.find(id);
    return callSource !== undefined && `${callSource.status}` !== STATUS.ENABLED;
  }

  updateCallSources(callSources: ICallSourceInfo[]): void {
    this.callSourceStore.batchUpdate(callSources);
  }

  @SignalrMessage('CallSourceMsg')
  protected onCallSourceMsgReceived(data: ICallSourceRespMsg): void {
    this.updateCallSources(data.callSources);
  }

  @SignalrMessage('CallSourceUpdateRespMsg')
  protected onCallSourceUpdateMsgReceived(data: ICallSourceUpdateRespMsg): void {
    this.updateCallSources([data.callSource]);
  }
}
